import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Columns3, FileText, Loader2, Sparkles } from 'lucide-react';

import { ErrorState } from '@/components/ui/ErrorState';
import { Spinner } from '@/components/ui/Spinner';
import { EnrichmentList } from '@/features/catalog/components/EnrichmentList';
import {
  useAsset,
  useEnrichments,
  useGenerateEnrichment,
  useReviewEnrichment,
} from '@/features/catalog/hooks';
import type { ApiError } from '@/lib/errors';

export function AssetDetailPage(): JSX.Element {
  const [params] = useSearchParams();
  const fqn = params.get('fqn') ?? '';

  const asset = useAsset(fqn);
  const enrichments = useEnrichments({ asset_fqn: fqn });
  const generate = useGenerateEnrichment();
  const review = useReviewEnrichment();

  if (fqn === '') {
    return (
      <p className="rounded-xl border border-dashed border-ink-300 dark:border-white/15 bg-white/60 dark:bg-white/5 p-6 text-sm text-ink-500 dark:text-ink-400">
        No asset specified.
      </p>
    );
  }
  if (asset.isPending) {
    return <Spinner label="Loading asset…" />;
  }
  if (asset.isError) {
    return <ErrorState error={asset.error as ApiError} />;
  }

  const table = asset.data;

  return (
    <div className="space-y-7">
      <Link
        to="/catalog"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-500 dark:text-ink-400 transition hover:text-brand-700"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to search
      </Link>

      <div>
        <div className="eyebrow eyebrow-dot mb-2">
          <span>ASSET</span>
        </div>
        <h1 className="h-display">{table.name}</h1>
        <p className="mt-2 font-mono text-xs text-ink-500 dark:text-ink-400">{table.fqn}</p>
      </div>

      <SectionShell icon={FileText} accent="brand" title="Description">
        <p className="text-sm leading-relaxed text-ink-700 dark:text-ink-200">
          {table.description ?? <span className="italic text-ink-400 dark:text-ink-500">No description.</span>}
        </p>
        {table.tags.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {table.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-ink-100 dark:bg-white/[0.06] px-2.5 py-0.5 font-mono text-[11px] font-medium text-ink-700 dark:text-ink-200 ring-1 ring-inset ring-ink-200"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </SectionShell>

      <SectionShell
        icon={Columns3}
        accent="sky"
        title="Columns"
        description={`${table.columns.length} column(s)`}
      >
        <div className="overflow-hidden rounded-lg border border-ink-200/70 dark:border-white/10">
          <table className="w-full">
            <thead className="bg-ink-50/70 dark:bg-white/[0.03]">
              <tr className="font-mono text-[10px] font-semibold tracking-[0.14em] text-ink-500 dark:text-ink-400">
                <th className="px-4 py-2.5 text-left">NAME</th>
                <th className="px-4 py-2.5 text-left">TYPE</th>
                <th className="px-4 py-2.5 text-left">TAGS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100 dark:divide-white/5">
              {table.columns.map((column) => (
                <tr key={column.name} className="transition hover:bg-brand-50/30">
                  <td className="px-4 py-2.5 font-mono text-[13px] font-medium text-ink-800 dark:text-ink-100">
                    {column.name}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-ink-500 dark:text-ink-400">{column.dataType}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-ink-500 dark:text-ink-400">
                    {column.tags.join(', ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionShell>

      <SectionShell
        icon={Sparkles}
        accent="violet"
        title="AI enrichment"
        description="Suggestions are reviewed here before they are written back to OpenMetadata."
      >
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={generate.isPending}
            onClick={() => generate.mutate({ fqn })}
            className="btn-primary h-9 px-3 text-xs"
          >
            {generate.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <Sparkles className="h-3 w-3" />
                Generate suggestions
              </>
            )}
          </button>
          {generate.isError && (
            <span className="font-mono text-xs text-accent-crimson">
              {(generate.error as ApiError).message}
            </span>
          )}
        </div>

        {enrichments.isPending ? (
          <Spinner />
        ) : enrichments.isError ? (
          <ErrorState error={enrichments.error as ApiError} />
        ) : (
          <EnrichmentList
            enrichments={enrichments.data}
            busyId={review.isPending ? review.variables.id : null}
            onApprove={(id) => review.mutate({ id, action: 'approve' })}
            onReject={(id) => review.mutate({ id, action: 'reject' })}
          />
        )}
      </SectionShell>
    </div>
  );
}

function SectionShell({
  icon: Icon,
  accent,
  title,
  description,
  children,
}: {
  icon: typeof FileText;
  accent: 'brand' | 'sky' | 'violet';
  title: string;
  description?: string;
  children: React.ReactNode;
}): JSX.Element {
  const meta = {
    brand: { bg: 'bg-brand-50 dark:bg-brand-900/30', fg: 'text-brand-600' },
    sky: { bg: 'bg-accent-sky/10', fg: 'text-accent-sky' },
    violet: { bg: 'bg-accent-violet/10', fg: 'text-accent-violet' },
  }[accent];
  return (
    <section className="rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 shadow-soft animate-slide-up">
      <header className="flex items-start gap-3 border-b border-ink-100 dark:border-white/5 px-6 py-4">
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${meta.bg} ${meta.fg}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <h3 className="h-section">{title}</h3>
          {description && <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">{description}</p>}
        </div>
      </header>
      <div className="p-6">{children}</div>
    </section>
  );
}
